<!--
  * Path: packages/frontend/src/core/components/common/NavigationLayout.vue
  * Task: Global application layout scaffolding providing app bars, sidebars, global dialogs, and URL/State synchronization.
  * Update: Unified entity selection across ALL views (including Browser) to use ?entity= query params.
-->
<template>
  <v-app>
    <AppMenubar />
    <AppSidebar v-if="isAuthenticated" />

    <!-- v-main applies the strict height constraints needed for internal scrollbars to work -->
    <v-main :class="isFixedLayout ? 'non-scrolling-main' : 'scrollable-main'" class="bg-background">
      <slot></slot>
    </v-main>

    <!-- GLOBAL DIALOGS & OVERLAYS -->
    <LoginDialog />
    <RegisterDialog />
    <UserProfileDialog />
    <InfoDialog />
    <NewProjectDialog />
    <OpenProjectDialog />
    <NewEntityDialog />
    <Notification />

    <!-- TERTIARY PREVIEW MODAL (Capability implemented only once) -->
    <v-dialog v-model="workspaceStore.isPreviewModalOpen" max-width="1000" scrollable>
      <v-card class="rounded-xl overflow-hidden bg-surface" style="height: 85vh; max-height: 85vh;">
        <BrowserSecondaryPanel context-type="preview" />
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useUiStore } from '@/store/ui';
import { useWorkspaceStore } from '@/store/workspace';
import { useProjectStore } from '@/store/project';
import { usePermissions } from '@/core/composables/usePermissions';

// UI Components
import AppMenubar from '@/core/components/app/AppMenubar.vue';
import AppSidebar from '@/core/components/app/AppSidebar.vue';

// Global Dialogs & Panels
import LoginDialog from '@/core/components/dialogs/LoginDialog.vue';
import RegisterDialog from '@/core/components/dialogs/RegisterDialog.vue';
import UserProfileDialog from '@/core/components/dialogs/UserProfileDialog.vue';
import InfoDialog from '@/core/components/dialogs/InfoDialog.vue';
import NewProjectDialog from '@/core/components/dialogs/NewProjectDialog.vue';
import OpenProjectDialog from '@/core/components/dialogs/OpenProjectDialog.vue';
import NewEntityDialog from '@/core/components/dialogs/NewEntityDialog.vue';
import Notification from '@/core/components/dialogs/Notification.vue';
import BrowserSecondaryPanel from '@/features/browser/components/BrowserSecondaryPanel.vue';

const route = useRoute();
const router = useRouter();
const uiStore = useUiStore();
const workspaceStore = useWorkspaceStore();
const projectStore = useProjectStore();
const { isAuthenticated } = usePermissions();

// Watch for entity selection changes to update the global UI title
watch(
  () => workspaceStore.selectedEntity,
  () => {
    uiStore.updateDynamicEntityTitle();
  },
  { immediate: true }
);

// All views supporting deep linking of the selected entity via query param
const viewsWithEntitySync = ['mapWeb', 'mapSequence', 'impactMap', 'mapStar', 'editor'];

// 1. Sync Store -> URL (Uniformly uses ?entity=... for ALL views)
watch(
  () => workspaceStore.selectedEntityId,
  (newId) => {
    if (!projectStore.loadedProject) return;
    if (route.name !== uiStore.activeView) return;

    if (viewsWithEntitySync.includes(uiStore.activeView)) {
      const currentQueryId = route.query.entity as string | undefined;
      if (newId !== currentQueryId) {
        router.replace({
          query: { ...route.query, entity: newId || undefined }
        }).catch(() => {});
      }
    }
  }
);

// 2. Sync URL -> Store
watch(
  () => route.query.entity,
  (newQueryId) => {
    if (viewsWithEntitySync.includes(uiStore.activeView)) {
      const targetId = newQueryId as string | undefined;
      if (targetId && targetId !== workspaceStore.selectedEntityId) {
        workspaceStore.setSelectedEntity(targetId);
      } else if (!targetId && workspaceStore.selectedEntityId) {
        workspaceStore.setSelectedEntity(null);
      }
    }
  },
  { immediate: true }
);

// Determines if the layout should be strictly fixed to the viewport
const isFixedLayout = computed<boolean>(() => {
  const fixedViews: string[] = [
    'editor',
    'mapWeb',
    'mapSequence',
    'mapIshikawa',
    'roledescription',
    'calendar',
    'matrix',
    'dataSettings',
    'projectAdministration',
    'impactMap',
    'kanban'
  ];
  return fixedViews.includes(uiStore.activeView);
});
</script>

<style scoped>
.bg-background {
  background-color: rgb(var(--v-theme-background));
}

.non-scrolling-main {
  height: calc(100vh - var(--v-layout-top));
  overflow-y: hidden;
  transition: height 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

.scrollable-main {
  height: calc(100vh - var(--v-layout-top));
  overflow-y: auto;
  transition: height 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}
</style>
